import React, { useEffect, useState } from "react";
import "../assets/comingsoon.css";
import PageContainer from "../pages/PageContainer";

/**
 * ComingSoon component to display a placeholder for pages under development
 * @param {Object} props - Component props
 * @param {string} props.pageName - Name of the page being developed
 * @param {boolean} props.withSidebar - Whether to render with sidebar spacing (default: true)
 * @returns {JSX.Element} Coming soon placeholder
 */
const ComingSoon = ({ pageName, withSidebar = true }) => {
  const [particles, setParticles] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(new Date().toLocaleDateString());
  const [randomFacts] = useState([
    "Our team is working around the clock.",
    "We're polishing every pixel.",
    "Almost there! Just a few more tweaks.",
    "Great things take time to build.",
  ]);
  const [currentFact, setCurrentFact] = useState(0);

  // Generate particles for the background effect
  useEffect(() => {
    const generateParticles = () => {
      const particlesArray = [];
      const particleCount = window.innerWidth > 768 ? 20 : 10;

      for (let i = 0; i < particleCount; i++) {
        particlesArray.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 10 + 2,
          opacity: Math.random() * 0.5 + 0.1,
          animationDuration: Math.random() * 20 + 10,
          animationDelay: Math.random() * 5,
        });
      }

      setParticles(particlesArray);
    };

    generateParticles();

    // Regenerate particles on window resize
    window.addEventListener("resize", generateParticles);
    return () => window.removeEventListener("resize", generateParticles);
  }, []);

  // Rotate through random facts
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFact((prev) => (prev + 1) % randomFacts.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [randomFacts.length]);

  const comingSoonContent = (
    <div className="coming-soon-container">
      {/* Background particles */}
      <div className="coming-soon-particles">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="coming-soon-particle"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              opacity: particle.opacity,
              animation: `float ${particle.animationDuration}s ease-in-out ${particle.animationDelay}s infinite`,
            }}
          />
        ))}
      </div>

      {/* Decorative SVG background */}
      <svg
        className="coming-soon-svg-bg"
        viewBox="0 0 200 200"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          top: 0,
          left: 0,
          zIndex: 0,
          opacity: 0.3,
        }}
      >
        <path
          fill="#1976d2"
          d="M45.7,-76.5C59.4,-70.2,71,-58.9,77.8,-45.2C84.6,-31.4,86.7,-15.7,84.9,-1.1C83.1,13.6,77.5,27.1,70.6,40.7C63.7,54.2,55.5,67.7,43.7,75.5C31.9,83.2,16,85.1,0.5,84.2C-14.9,83.3,-29.8,79.7,-43.2,72.5C-56.6,65.3,-68.5,54.7,-75.7,41.4C-82.9,28.1,-85.3,14.1,-83,1.3C-80.7,-11.4,-73.6,-22.8,-65.3,-32.8C-56.9,-42.7,-47.3,-51.2,-36.6,-59.1C-25.9,-67,-13,-74.2,1.3,-76.4C15.6,-78.5,31.1,-82.8,45.7,-76.5Z"
          transform="translate(100 100)"
        >
          <animateTransform
            attributeName="transform"
            type="translate"
            values="100 100; 100 105; 100 100"
            dur="10s"
            repeatCount="indefinite"
          />
        </path>
      </svg>

      {/* Content */}
      <h2 className="coming-soon-title">Coming Soon</h2>
      <h1 className="coming-soon-name">{pageName || "New Feature"}</h1>
      <p className="coming-soon-message">
        We're working hard to bring you an amazing experience. This page is
        currently under development and will be available soon.
      </p>

      {/* Progress indicator */}
      <div className="coming-soon-progress">
        <div className="coming-soon-progress-bar" />
      </div>

      {/* Random facts notification */}
      <div
        className="coming-soon-notification"
        style={{ animation: "pulse 2s infinite ease-in-out" }}
      >
        <span className="coming-soon-notification-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="#5d87ff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 6V12L16 14"
              stroke="#5d87ff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="coming-soon-notification-text">
          {randomFacts[currentFact]}
        </span>
      </div>

      {/* Last update notification */}
      <div className="coming-soon-notification" style={{ marginTop: "1rem" }}>
        <span className="coming-soon-notification-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 8V12L14 14"
              stroke="#5d87ff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3.05078 11.0002C3.27246 8.18293 4.51515 5.54987 6.53214 3.66492C8.54912 1.77998 11.1949 0.81136 13.9257 0.969078C16.6564 1.1268 19.1643 2.39748 20.9903 4.5002C22.8164 6.60291 23.8151 9.3734 23.7775 12.2252C23.7399 15.077 22.6687 17.8232 20.7664 19.878C18.8641 21.9329 16.3114 23.1392 13.5709 23.2284C10.8304 23.3175 8.20545 22.2807 6.15868 20.3349C4.1119 18.3891 2.82987 15.6834 2.68945 12.816"
              stroke="#5d87ff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M3 4V8H7"
              stroke="#5d87ff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="coming-soon-notification-text">
          Last updated: {lastUpdate}
        </span>
      </div>
    </div>
  );

  return (
    <PageContainer withSidebar={withSidebar}>{comingSoonContent}</PageContainer>
  );
};

export default ComingSoon;
